import { useMemo } from 'react';
import { extractUrls } from '@frontend/components';
import { useTeamChatSelector } from '../providers/team-chat.provider';
import { getUserFullName } from '../utils';

/**
 * Utility function to get all users including current user, filtered to remove null/undefined values
 */
const getAllValidUsers = (users: any[] | null | undefined, currentUser: any) => {
  return [...(users ?? []), currentUser].filter(Boolean);
};

/**
 * Hook to get user mentions (usernames and user IDs)
 */
export const useUserMentions = () => {
  const users = useTeamChatSelector((ctx) => ctx.users);
  const currentUser = useTeamChatSelector((ctx) => ctx.user);

  const allValidUsers = useMemo(() => getAllValidUsers(users, currentUser), [users, currentUser]);

  const usernames = useMemo(() => {
    return allValidUsers.reduce<string[]>((acc, user) => {
      if (user) {
        acc.push(`@${getUserFullName(user.firstName, user.lastName)}`);
      }
      return acc;
    }, []);
  }, [allValidUsers]);

  const userIds = useMemo(() => {
    return allValidUsers.reduce<string[]>((acc, user) => {
      if (user) {
        acc.push(`@${user.userID}`);
      }
      return acc;
    }, []);
  }, [allValidUsers]);

  return {
    users: users ?? [],
    currentUser,
    usernames,
    userIds,
  };
};

/**
 * Hook to extract all parseable content from message text
 */
export const useMessageContent = (message: string) => {
  const { usernames, userIds } = useUserMentions();

  const urls = useMemo(() => extractUrls(message), [message]);

  const allDelimiters = useMemo(() => [...usernames, ...userIds, ...urls], [usernames, userIds, urls]);

  return {
    urls,
    allDelimiters,
  };
};
